import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import {
  Button,
  Table,
  Spin,
  DatePicker,
  Select
} from 'antd';
import {
  notificationsAtom
} from '../../../../../atoms/Atoms';
import Card from '../../../../shared-components/container/Card';
import {
  getDatePresets,
  thousand,
  toCurrency,
  getCloserSummaryTableColumns
} from '../../../../../functions';
import api from '../../../../../api/api';

function OrgClosersSummaryTable({ orgCode, useGlobal }) {
  const referralCode = orgCode;
  const { RangePicker } = DatePicker;
  const { Option } = Select;
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState('');

  function getSummary(page) {
    if (!dateRange.start || !dateRange.end) {
      return;
    }
    setCurrentPage(page);
    setLoading(true);
    const startDate = dayjs(dateRange.start);
    const endDate = dayjs(dateRange.end);
    const startString = startDate.add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    const endString = endDate.add(4, 'hour').add(-1, 'second').format('YYYY-MM-DDTHH:mm:ss');
    let url = `reports/summary/closers?startDate=${startString}&endDate=${endString}`;
    if (referralCode) {
      url += `&orgReferralCode=${referralCode}`;
    }
    url += `&orgId=${selectedOrganization}`;
    api.get(url)
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setSummary(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  const onTableChange = (page, size) => {
    if (page - 1 !== currentPage) {
      setCurrentPage(page - 1);
      getSummary(page - 1);
    } else {
      setPageSize(size);
    }
  };

  function getOrganizations() {
    setLoadingOrganizations(true);
    api.get('organizations/active')
      .then((response) => {
        setLoadingOrganizations(false);
        if (response?.data?.success) {
          setOrganizations(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoadingOrganizations(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      })
  }

  useEffect(() => {
    setDateRange({
      start: dayjs().startOf('week').format('MMM DD, YYYY'),
      end: dayjs().endOf('day').format('MMM DD, YYYY')
    });
    if (useGlobal) {
      getOrganizations();
    }
  }, []);

  useEffect(() => {
    getSummary(0);
  }, [dateRange, selectedOrganization]);

  useEffect(() => {
    getSummary(0);
  }, [pageSize]);

  return (
    <div>
      <div className="flex space-x-2 mb-2">
        {useGlobal && (
          <Select
            showSearch
            style={{ width: 200 }}
            className="ml-auto h-12"
            filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
            value={selectedOrganization}
            loading={loadingOrganizations}
            disabled={loading}
            onChange={(obj) => setSelectedOrganization(obj)}
          >
            <Option value="">{t('any_organization')}</Option>
            <Option value="-1">{t('no_organization')}</Option>
            {_.map(organizations, (c) => (
              <Option value={c.id}>{c.nameEn}</Option>
            ))}
          </Select>
        )}
        <RangePicker
          className="bg-transparent ml-auto"
          presets={getDatePresets(t)}
          disabled={loading}
          value={[
            dayjs(dateRange?.start),
            dayjs(dateRange?.end)
          ]}
          onChange={(obj) => {
            setDateRange({
              start: obj[0],
              end: obj[1]
            });
          }}
          format="MMM DD, YYYY"
        />
        <Button type="primary" loading={loading} onClick={() => getSummary(currentPage)}>{t('reload')}</Button>
      </div>
      <div className="grid gap-4 my-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        <Spin spinning={loading}>
          <Card
            className="col-span-1 w-full border-4 rounded-sm"
            title={t('all_opportunities')}
            description={thousand(summary?.totalOpportunities)}
          />
        </Spin>
        <Spin spinning={loading}>
          <Card
            className="col-span-1 w-full border-4 rounded-sm"
            title={t('self-gen')}
            description={thousand(summary?.totalSelfGen)}
          />
        </Spin>
        <Spin spinning={loading}>
          <Card
            className="col-span-1 w-full border-4 rounded-sm"
            title={t('referrals_by_customer')}
            description={thousand(summary?.totalReferralsByCustomer)}
          />
        </Spin>
        <Spin spinning={loading}>
          <Card
            className="col-span-1 w-full border-4 rounded-sm"
            title={t('closer_setter')}
            description={thousand(summary?.totalCloserSetter)}
          />
        </Spin>
        <Spin spinning={loading}>
          <Card
            className="col-span-1 w-full border-4 rounded-sm"
            title={t('leads')}
            description={thousand(summary?.totalLeads)}
          />
        </Spin>
        <Spin spinning={loading}>
          <Card
            className="col-span-1 w-full border-4 rounded-sm"
            title={t('deals')}
            description={thousand(summary?.totalDeals)}
          />
        </Spin>
        <Spin spinning={loading}>
          <Card
            className="col-span-1 w-full border-4 rounded-sm"
            title={t('signed')}
            description={thousand(summary?.totalSigned)}
          />
        </Spin>
        <Spin spinning={loading}>
          <Card
            className="col-span-1 w-full border-4 rounded-sm"
            title={t('installs')}
            description={thousand(summary?.totalInstalls)}
          />
        </Spin>
        {/* <Spin spinning={loading}>
          <Card
            className="col-span-1 w-full border-4 rounded-sm"
            title={t('activations')}
            description={thousand(summary?.totalActivations)}
          />
        </Spin> */}
        <Spin spinning={loading}>
          <Card
            className="col-span-1 w-full border-4 rounded-sm"
            title={t('lost')}
            description={thousand(summary?.totalLost)}
          />
        </Spin>
      </div>
      <Table
        rowKey="userId"
        locale={{
          emptyText: t('no_data')
        }}
        pagination={{
          pageSize,
          showSizeChanger: true,
          current: currentPage + 1,
          total: summary?.totalUsers,
          onChange: onTableChange
        }}
        bordered
        loading={loading}
        dataSource={summary?.summary}
        columns={getCloserSummaryTableColumns(t)}
      />
    </div>
  );
}

export default OrgClosersSummaryTable;
